import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { User, Sparkles, FileText, Camera, Award, MessageSquare, CheckCircle2, ArrowRight } from 'lucide-react';

const ProfileBuilding = () => {
  const process = [
    {
      step: '01',
      icon: MessageSquare,
      title: 'Discovery Call',
      description: 'We learn about your background, goals, and target audience in a 30-minute consultation.'
    },
    {
      step: '02',
      icon: FileText,
      title: 'Content Creation',
      description: 'Our writers craft compelling copy that showcases your unique value and achievements.'
    },
    {
      step: '03',
      icon: Camera,
      title: 'Visual Design',
      description: 'Custom banner design and photo recommendations to make your profile stand out.'
    },
    {
      step: '04',
      icon: Sparkles,
      title: 'Optimization',
      description: 'Strategic keyword placement and formatting for maximum recruiter visibility.'
    },
    {
      step: '05',
      icon: Award,
      title: 'Launch & Support',
      description: 'Profile goes live with 30-day support for adjustments and strategy guidance.'
    }
  ];

  const includes = [
    'Professional headline that grabs attention',
    'Compelling "About" section (150-200 words)',
    'Experience section optimization (up to 5 roles)',
    'Skills selection and endorsement strategy',
    'Education and certifications formatting',
    'Custom LinkedIn banner design',
    'Profile photo guidelines and tips',
    'SEO optimization for recruiter searches',
    'Recommendation request templates',
    'Content posting strategy guide',
    '30-day email support',
    'One revision round included'
  ];

  const benefits = [
    { title: '5-7 Days', description: 'Delivery Time' },
    { title: '100%', description: 'Satisfaction Guarantee' },
    { title: '1-on-1', description: 'Personal Attention' }
  ];

  return (
    <div className="pt-16">
      <section className="py-32 bg-gradient-to-br from-primary-dark via-primary-main to-primary-dark text-text-inverse" data-testid="profile-hero">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="inline-block text-secondary-main font-body uppercase tracking-widest text-xs mb-4" data-testid="hero-subtitle">
                Professional Profile Building
              </span>
              <h1 className="font-heading text-4xl lg:text-6xl font-bold mb-6 leading-tight" data-testid="hero-title">
                Get a <span className="italic text-secondary-main">World-Class</span> LinkedIn Profile Built for You
              </h1>
              <p className="text-text-inverse/80 text-xl leading-relaxed mb-8" data-testid="hero-description">
                Don't have time to optimize your profile? Let our expert writers and designers create a stunning LinkedIn presence that attracts opportunities.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="#pricing"
                  data-testid="hero-cta-primary"
                  className="bg-secondary-main text-primary-dark hover:bg-secondary-light rounded-none px-8 py-4 uppercase tracking-widest text-xs font-semibold inline-flex items-center justify-center gap-2 transition-all"
                >
                  See Pricing
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/enquiry"
                  data-testid="hero-cta-secondary"
                  className="border border-text-inverse text-text-inverse hover:bg-white/10 rounded-none px-8 py-4 uppercase tracking-widest text-xs font-semibold inline-flex items-center justify-center transition-all"
                >
                  Book Consultation
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="grid grid-cols-3 gap-4 mt-16"
            >
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm p-6 border border-white/20" data-testid={`benefit-${index}`}>
                  <div className="font-heading text-3xl font-bold mb-1">{benefit.title}</div>
                  <div className="text-sm text-text-inverse/80">{benefit.description}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl lg:text-5xl font-bold text-primary-main mb-4" data-testid="process-title">
              Our 5-Step Profile Building Process
            </h2>
            <p className="text-text-secondary text-lg max-w-3xl mx-auto">
              From discovery to launch, we handle everything to create your perfect LinkedIn profile.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {process.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative"
                  data-testid={`process-step-${index}`}
                >
                  <div className="bg-background-main border border-primary-main/10 p-6 h-full hover:shadow-lg transition-all">
                    <div className="text-secondary-main font-heading text-4xl font-bold mb-3">{item.step}</div>
                    <Icon className="w-8 h-8 text-secondary-main mb-3" />
                    <h3 className="font-body font-semibold text-primary-main mb-2 text-sm">{item.title}</h3>
                    <p className="text-text-secondary text-xs leading-relaxed">{item.description}</p>
                  </div>
                  {index < process.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                      <ArrowRight className="w-6 h-6 text-secondary-main" />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-background-main" data-testid="includes-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-4xl font-bold text-primary-main mb-6" data-testid="includes-title">
                Everything You Need for a Professional Profile
              </h2>
              <p className="text-text-secondary leading-relaxed mb-6">
                Our comprehensive service covers every aspect of your LinkedIn profile, ensuring you present the best version of yourself to recruiters, clients, and your network.
              </p>
              <div className="bg-primary-dark text-text-inverse p-6">
                <h3 className="font-heading text-xl font-bold mb-3">Why Professional Profile Building?</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-secondary-main flex-shrink-0 mt-0.5" />
                    <span>Save 10-15 hours of your valuable time</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-secondary-main flex-shrink-0 mt-0.5" />
                    <span>Professional writers know what recruiters look for</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-secondary-main flex-shrink-0 mt-0.5" />
                    <span>Avoid common mistakes that hurt visibility</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-secondary-main flex-shrink-0 mt-0.5" />
                    <span>Get objective perspective on your strengths</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="bg-white p-8 border border-primary-main/10">
              <h3 className="font-heading text-2xl font-bold text-primary-main mb-6">What's Included</h3>
              <div className="space-y-3">
                {includes.map((item, index) => (
                  <div key={index} className="flex items-start gap-3" data-testid={`include-${index}`}>
                    <CheckCircle2 className="w-5 h-5 text-secondary-main flex-shrink-0 mt-0.5" />
                    <span className="text-text-secondary text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="py-20 bg-white" data-testid="pricing-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl lg:text-5xl font-bold text-primary-main mb-4">
              Professional Profile Building
            </h2>
            <p className="text-text-secondary text-lg">
              One comprehensive package with everything you need.
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-primary-main to-primary-light text-text-inverse p-12 shadow-2xl"
            data-testid="pricing-card"
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
              <div>
                <h3 className="font-heading text-3xl font-bold mb-2">Complete Profile Package</h3>
                <p className="text-text-inverse/80">Done-for-you LinkedIn profile optimization</p>
              </div>
              <div className="mt-4 md:mt-0">
                <div className="font-heading text-6xl font-bold">$499</div>
                <div className="text-text-inverse/80 text-sm">one-time payment</div>
              </div>
            </div>
            <div className="border-t border-white/20 pt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {includes.slice(0, 6).map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-secondary-main flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <div className="text-center">
                <Link
                  to="/enquiry"
                  className="bg-secondary-main text-primary-dark hover:bg-secondary-light rounded-none px-12 py-4 uppercase tracking-widest text-sm font-semibold inline-block transition-all"
                >
                  Get Started Today
                </Link>
                <p className="text-text-inverse/60 text-xs mt-4">5-7 day turnaround • 100% satisfaction guarantee</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-background-main">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-4xl font-bold text-primary-main mb-6">
            Questions About Profile Building?
          </h2>
          <p className="text-text-secondary text-lg mb-8">
            Book a free 15-minute consultation to discuss your needs and see if our service is right for you.
          </p>
          <Link
            to="/enquiry"
            className="border-2 border-primary-main text-primary-main hover:bg-primary-main hover:text-white rounded-none px-8 py-4 uppercase tracking-widest text-xs font-semibold inline-block transition-all"
          >
            Schedule Free Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProfileBuilding;